import { useTranslation } from "react-i18next";
import { createMutualFollowCodeGQL } from "@onmo/graphql/legacyGraphql";
import { useLegacyMutation } from "@onmo/graphql/legacyGraphql";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { isIOS } from "react-device-detect";
import { Query, User } from "@onmo/graphql/graphql";
import {
  followSuggestionsGQL,
  followUserGQL,
  getFollowerUsersGQL,
  getFollowingUsersGQL,
  meGQL,
  unfollowUserGQL,
} from "../../../graphql";
import { useCallback, useEffect } from "react";
import { getNewFollowStatus } from "../../../models/friend";

import { NB_ITEM_LOAD_MORE } from "../../../constants/friend";
import {
  updateTotalFollowers,
  updateTotalFollowing,
  updateTotalFollowSuggestions,
} from "../../../store/slices/friends";
import { useAppDispatch } from "../../../components/hooks";

export const useShareUser = () => {
  const { t } = useTranslation();
  const [createOncode] = useLegacyMutation(createMutualFollowCodeGQL);
  const { data: dataMe } = useQuery(meGQL);

  const shareUser = useCallback(async () => {
    try {
      const { data: oncodeData } = await createOncode();
      const inviteUrl = `${window.location.origin}/profile/${dataMe?.me?.id}?oncode=${oncodeData?.createMutualFollowCode?.oncode}`;
      const appName = import.meta.env.VITE_APP_NAME || "Gaming Platform";
      const inviteText = t("common_Come play with me in", {
        appName,
        username: dataMe?.me?.username,
      });
      const shareData = {
        title: appName,
        url: inviteUrl,
        text: inviteText,
      };
      if (isIOS) {
        await navigator.share({
          title: appName,
          url: window.location.href,
        });
      } else {
        await window.navigator.share(shareData);
      }
    } catch (error) {
      console.error(error);
    }
  }, [createOncode, dataMe?.me?.id, dataMe?.me?.username, t]);

  return shareUser;
};

export const useFollowUser = (user: User) => {
  const [followUserAction] = useMutation(followUserGQL);
  const { data: dataMe } = useQuery(meGQL);
  const [fetchFollowing] = useLazyQuery<Query>(getFollowingUsersGQL);

  const followUser = useCallback(() => {
    followUserAction({
      variables: { userId: user.id },
      update(cache) {
        const userWithNewFollowStatus = {
          ...user,
          followStatus: getNewFollowStatus(user.followStatus),
        };

        // Update the friend cache
        const friendCacheId = cache.identify({
          __typename: "User",
          id: user.id,
        });
        if (friendCacheId) {
          cache.modify({
            id: friendCacheId,
            fields: {
              followStatus() {
                return getNewFollowStatus(user.followStatus);
              },
            },
          });
        }

        // Update the following list
        const dataFollowing = cache.readQuery<Query>({
          query: getFollowingUsersGQL,
          variables: { id: dataMe?.me?.id },
        });
        if (!dataFollowing?.user.following) {
          fetchFollowing({ variables: { id: dataMe?.me?.id } });
        } else {
          const newDataFollow = {
            ...dataFollowing,
            user: {
              ...dataFollowing.user,
              following: {
                ...dataFollowing.user.following,
                total: dataFollowing.user.following.total + 1,
                items: [
                  { user: userWithNewFollowStatus },
                  ...dataFollowing.user.following.items,
                ],
              },
            },
          };
          cache.writeQuery({
            query: getFollowingUsersGQL,
            variables: { id: dataMe?.me?.id },
            data: newDataFollow,
          });
        }
      },
    });
  }, [dataMe?.me?.id, fetchFollowing, followUserAction, user]);
  return followUser;
};
export const useUnfollowUser = (user: User) => {
  const [unfollowUserAction] = useMutation(unfollowUserGQL);

  const followUser = useCallback(() => {
    unfollowUserAction({
      variables: { userId: user.id },
      update(cache) {
        // Update the friend cache
        const friendCacheId = cache.identify({
          __typename: "User",
          id: user.id,
        });
        if (friendCacheId) {
          cache.modify({
            id: friendCacheId,
            fields: {
              followStatus() {
                return getNewFollowStatus(user.followStatus, {
                  unfollow: true,
                });
              },
            },
          });
        }
      },
    });
  }, [unfollowUserAction, user]);
  return followUser;
};

export const useFetchFollowing = () => {
  const dispatch = useAppDispatch();
  const { data: dataMe } = useQuery(meGQL);
  const {
    data: dataFollowing,
    fetchMore,
    loading,
  } = useQuery<Query>(getFollowingUsersGQL, {
    skip: !dataMe?.me?.id,
    variables: {
      id: dataMe?.me?.id,
      input: {
        page: {
          limit: NB_ITEM_LOAD_MORE,
          offset: 0,
        },
      },
    },
  });
  const followingResponse = dataFollowing?.user?.following;

  const loadMore = async () => {
    if (
      !followingResponse ||
      followingResponse?.items.length >= followingResponse?.total
    )
      return;
    await fetchMore({
      variables: {
        id: dataMe?.me?.id,
        input: {
          page: {
            limit: NB_ITEM_LOAD_MORE,
            offset: followingResponse.items.length,
          },
        },
      },
    });
  };

  useEffect(() => {
    dispatch(
      updateTotalFollowing({
        totalFollowing: followingResponse?.total || 0,
      }),
    );
  }, [dispatch, followingResponse?.total]);

  return {
    loadMore,
    followingResponse,
    loading,
  };
};

export const useFetchFollowers = () => {
  const dispatch = useAppDispatch();
  const { data: dataMe } = useQuery(meGQL);
  const {
    data: dataFollowers,
    fetchMore,
    loading,
  } = useQuery<Query>(getFollowerUsersGQL, {
    skip: !dataMe?.me?.id,
    variables: {
      id: dataMe?.me?.id,
      input: {
        page: {
          limit: NB_ITEM_LOAD_MORE,
          offset: 0,
        },
      },
    },
  });
  const followersResponse = dataFollowers?.user?.followers;

  const loadMore = async () => {
    if (
      !followersResponse ||
      followersResponse?.items.length >= followersResponse?.total
    )
      return;
    await fetchMore({
      variables: {
        id: dataMe?.me?.id,
        input: {
          page: {
            limit: NB_ITEM_LOAD_MORE,
            offset: followersResponse.items.length,
          },
        },
      },
    });
  };

  useEffect(() => {
    dispatch(
      updateTotalFollowers({
        totalFollowers: followersResponse?.total || 0,
      }),
    );
  }, [dispatch, followersResponse?.total]);

  return {
    loadMore,
    followersResponse,
    loading,
  };
};
export const useFetchFollowSuggestions = () => {
  const dispatch = useAppDispatch();
  const { data: dataFollowSuggestions, refetch } =
    useQuery<Query>(followSuggestionsGQL);

  useEffect(() => {
    dispatch(
      updateTotalFollowSuggestions({
        totalFollowSuggestions:
          dataFollowSuggestions?.followSuggestions?.length || 0,
      }),
    );
  }, [dataFollowSuggestions?.followSuggestions?.length, dispatch]);

  return {
    followSuggestions: dataFollowSuggestions?.followSuggestions || [],
    refetch,
  };
};
